"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { CheckCircle2, Sparkles, Settings, Database, Zap, Loader2 } from "lucide-react";
import MindalooLogo from "@/components/branding/MindalooLogo";
import { db } from "@/lib/db";
import type { AITemplate } from "@/lib/templates/templates";
import type { TemplatePreset } from "@/types/templatePreset";
import type { AiProfile } from "@/types/ai";
import { getTemplatePresetById } from "@/lib/templates/presets";

interface Props {
  intent: string;
  templateId?: string | null;
  template?: AITemplate | TemplatePreset | null;
}

export default function BuilderOverview({ intent, templateId, template }: Props) {
  const router = useRouter();
  const [detectedType, setDetectedType] = useState<string>("");
  const [isVisible, setIsVisible] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedProfile, setGeneratedProfile] = useState<AiProfile | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [readyJustTurnedOn, setReadyJustTurnedOn] = useState(false);
  const prevGeneratedRef = useRef<AiProfile | null>(null);

  useEffect(() => {
    if (generatedProfile && !prevGeneratedRef.current) {
      setReadyJustTurnedOn(true);
      const t = setTimeout(() => setReadyJustTurnedOn(false), 1200);
      prevGeneratedRef.current = generatedProfile;
      return () => clearTimeout(t);
    }
    prevGeneratedRef.current = generatedProfile;
  }, [generatedProfile]);

  useEffect(() => {
    // Use template if provided, otherwise auto-detect
    if (template) {
      // Handle both old AITemplate and new TemplatePreset
      if ('visual' in template) {
        // New TemplatePreset
        setDetectedType(template.name);
      } else {
        // Old AITemplate
        setDetectedType(template.name);
      }
    } else {
      // Auto-detect AI type from intent (check more specific patterns first)
      const lowerIntent = intent.toLowerCase();
      
      // Content creation patterns (check first - more specific)
      if (lowerIntent.includes("write") || lowerIntent.includes("email") || lowerIntent.includes("content") || 
          lowerIntent.includes("blog") || lowerIntent.includes("article") || lowerIntent.includes("document")) {
        setDetectedType("Content Creation");
      } 
      // Customer support (only if explicitly mentioned, not just "help")
      else if (lowerIntent.includes("customer") || lowerIntent.includes("support") || 
               (lowerIntent.includes("help") && (lowerIntent.includes("customer") || lowerIntent.includes("client")))) {
        setDetectedType("Customer Support");
      } 
      // Knowledge/assistant patterns
      else if (lowerIntent.includes("knowledge") || lowerIntent.includes("assistant") || lowerIntent.includes("personal")) {
        setDetectedType("Knowledge Assistant");
      } 
      // Strategy/business patterns
      else if (lowerIntent.includes("strategy") || lowerIntent.includes("business") || lowerIntent.includes("plan")) {
        setDetectedType("Strategy Partner");
      } 
      // Generic help (fallback)
      else if (lowerIntent.includes("help")) {
        setDetectedType("Assistant");
      } 
      else {
        setDetectedType("Custom AI");
      }
    }

    // Fade in immediately (200ms)
    setTimeout(() => setIsVisible(true), 10);

    // Auto-generate AI profile
    generateProfile();
  }, [intent, template]);

  async function generateProfile() {
    if (generating || generatedProfile) return;
    
    setGenerating(true);
    setGenerateError(null);
    try {
      // Get user ID for ownership
      const { getUserId } = await import("@/lib/user/identity");
      const userId = getUserId();
      
      // Pass template object or templateId so API can use the right preset (role-focused AI)
      const templatePayload = template ?? templateId ?? undefined;
      const response = await fetch("/api/builder/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intent, template: templatePayload, userId }),
      });

      const data = await response.json();
      
      // Use fallback if there's an error but a fallback is provided
      const profile = data.profile || data.fallback;
      
      // If there's an error but we have a fallback, show a warning but continue
      if (data.error && profile) {
        console.warn("API returned error but using fallback profile:", data.error);
        // Don't show alert for fallback - just use it silently
      }
      
      // Always use profile if available, regardless of response status
      // The API route returns 200 with fallback profiles for errors
      if (!profile) {
        // Only throw if we truly have no profile
        const errorData = data.error || `Failed to generate profile: ${response.status}`;
        console.error("Builder API error - no profile available:", errorData);
        throw new Error(errorData);
      }
      
      // If there's an error message but we have a profile, log it but continue
      if (data.error && profile) {
        console.warn("API returned error but using profile:", data.error);
      }
      
      if (profile) {
        // Save to database
        try {
          if (typeof window !== "undefined" && db && db.aiProfiles) {
            await db.aiProfiles.add(profile);
            
            // Initialize KARMA Profile for this instance
            const { initializeKarmaProfile } = await import("@/lib/karma/profileManager");
            await initializeKarmaProfile(profile);
            console.log("✅ KARMA Profile initialized for:", profile.name);
          }
        } catch (error) {
          console.error("Failed to save profile:", error);
          // Continue even if save fails
        }
        
        setGeneratedProfile(profile);
      } else {
        throw new Error("No profile generated");
      }
    } catch (error: any) {
      console.error("Failed to generate profile:", error);
      setGenerateError(error.message || "Something went wrong. Please try again.");
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Transparent Navigation */}
      <nav className="relative z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <MindalooLogo size="md" showText={true} variant="light" />
          <div className="flex items-center gap-3">
            <button
              onClick={() => router.push("/home")}
              className="px-4 py-2 text-white/70 hover:text-white transition text-sm font-medium"
            >
              Home
            </button>
          </div>
        </div>
      </nav>

      {/* Builder Overview Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-20">
        <div className="w-full max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Progress Steps — driven by generating / generatedProfile */}
            <div className="lg:col-span-1 space-y-6">
              <div 
                className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 transition-all duration-200 ${
                  isVisible ? "opacity-100 scale-100" : "opacity-0 scale-[0.98]"
                }`}
              >
                <h3 className="text-sm font-semibold text-white/90 mb-4 uppercase tracking-wider">Progress</h3>
                <div className="relative">
                  {/* Vertical connector line */}
                  <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="w-full bg-gradient-to-b from-indigo-500 to-violet-500 transition-all duration-500 ease-out rounded-full"
                      style={{ height: generatedProfile ? "100%" : generating ? "66%" : "33%" }}
                    />
                  </div>
                  <div className="space-y-0">
                    {/* Step 1: Intent Captured — always complete */}
                    <div className="flex items-start gap-3 relative">
                      <div className="relative z-10 w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center shrink-0 ring-2 ring-indigo-400/50 ring-offset-2 ring-offset-transparent">
                        <CheckCircle2 className="w-5 h-5 text-white" />
                      </div>
                      <div className="pt-0.5 pb-4">
                        <div className="text-sm font-medium text-white">Intent Captured</div>
                        <div className="text-xs text-white/60">Understanding your needs</div>
                      </div>
                    </div>
                    {/* Step 2: Configuring — active when generating, complete when profile exists */}
                    <div className="flex items-start gap-3 relative">
                      <div 
                        className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                          generatedProfile 
                            ? "bg-indigo-500 ring-2 ring-indigo-400/50 ring-offset-2 ring-offset-transparent" 
                            : generating 
                              ? "bg-violet-500/90 ring-2 ring-violet-400 ring-offset-2 ring-offset-transparent animate-pulse" 
                              : "bg-white/10 border-2 border-white/20"
                        }`}
                      >
                        {generatedProfile ? (
                          <CheckCircle2 className="w-5 h-5 text-white" />
                        ) : generating ? (
                          <Loader2 className="w-5 h-5 text-white animate-spin" />
                        ) : (
                          <div className="w-2 h-2 rounded-full bg-white/40" />
                        )}
                      </div>
                      <div className="pt-0.5 pb-4">
                        <div className={`text-sm font-medium transition-colors duration-300 ${generatedProfile ? "text-white" : generating ? "text-white" : "text-white/70"}`}>
                          Configuring
                        </div>
                        <div className="text-xs text-white/60">
                          {generating ? "Setting up your AI..." : "Setting up your AI"}
                        </div>
                      </div>
                    </div>
                    {/* Step 3: Ready — lights up when profile is generated; play complete animation once */}
                    <div className="flex items-start gap-3 relative">
                      <div 
                        className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all duration-500 ${
                          generatedProfile 
                            ? "bg-indigo-500 ring-2 ring-indigo-400/50 ring-offset-2 ring-offset-transparent shadow-lg shadow-indigo-500/30" 
                            : "bg-white/5 border border-white/10"
                        } ${readyJustTurnedOn ? "animate-ready-complete" : ""}`}
                      >
                        {generatedProfile ? (
                          <CheckCircle2 className="w-5 h-5 text-white" />
                        ) : (
                          <div className={`w-2 h-2 rounded-full ${generating ? "bg-white/30" : "bg-white/20"}`} />
                        )}
                      </div>
                      <div className="pt-0.5">
                        <div className={`text-sm font-medium transition-all duration-300 ${generatedProfile ? "text-white" : "text-white/50"}`}>
                          Ready
                        </div>
                        <div className="text-xs text-white/60">Customise & deploy</div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Micro-copy: next step */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-xs text-white/50">
                    {generating
                      ? "Next: we'll have your AI ready in a moment."
                      : generatedProfile
                        ? "Next: pick colours, tone and personality — then deploy."
                        : generateError
                          ? "Next: try again or adjust your intent."
                          : "Next: we'll detect your AI type and build a profile."}
                  </p>
                </div>
              </div>
            </div>

            {/* Center: AI Summary Card */}
            <div className="lg:col-span-2">
              <div 
                className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 transition-all duration-200 ${
                  isVisible ? "opacity-100 scale-100" : "opacity-0 scale-[0.98]"
                }`}
              >
                <div className="space-y-6">
                  {/* Header */}
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <MindalooLogo size="lg" showText={false} variant="light" />
                      <div>
                        <h2 className="text-2xl font-semibold text-white">Your AI</h2>
                        {detectedType && (
                          <p className="text-sm text-white/60">Detected: {detectedType}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Confirmation Message */}
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <div className="text-sm font-semibold text-white mb-1">
                      Building your {detectedType || "AI"}
                    </div>
                    <p className="text-xs text-white/60 leading-relaxed">
                      Get ready to customize your AI with colors, tone, and personality.
                    </p>
                  </div>

                  {/* Intent Display */}
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <div className="text-xs font-medium text-white/60 mb-2 uppercase tracking-wider">Intent</div>
                    <p className="text-base text-white/90 leading-relaxed">{intent}</p>
                  </div>

                  {/* AI Configuration Summary */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-indigo-400" />
                        <div className="text-xs font-medium text-white/60 uppercase tracking-wider">Type</div>
                      </div>
                      <div className="text-sm font-medium text-white">{detectedType || "Custom"}</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex items-center gap-2 mb-2">
                        <Settings className="w-4 h-4 text-violet-400" />
                        <div className="text-xs font-medium text-white/60 uppercase tracking-wider">Tone</div>
                      </div>
                      <div className="text-sm font-medium text-white">
                        {template && 'tone' in template && typeof template.tone === 'object' 
                          ? template.tone.responseStyle?.greetingStyle || "Professional, Calm"
                          : template && 'tone' in template && typeof (template as { tone?: string }).tone === 'string'
                          ? (template as { tone: string }).tone 
                          : "Professional, Calm"}
                      </div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex items-center gap-2 mb-2">
                        <Database className="w-4 h-4 text-slate-400" />
                        <div className="text-xs font-medium text-white/60 uppercase tracking-wider">Data</div>
                      </div>
                      <div className="text-sm font-medium text-white">Private by default</div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                        <div className="text-xs font-medium text-white/60 uppercase tracking-wider">Status</div>
                      </div>
                      <div className="text-sm font-medium text-white">Ready to customize</div>
                    </div>
                  </div>

                  {generateError && (
                      <div className="mb-4 p-4 rounded-lg bg-red-500/20 border border-red-500/40 text-red-200 text-sm" role="alert">
                        {generateError}
                      </div>
                    )}
                    {/* Action Button */}
                  <div className="pt-4">
                    {generating ? (
                      <button
                        disabled
                        className="w-full px-6 py-3.5 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-600 text-white font-semibold opacity-75 cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Building your AI...
                      </button>
                    ) : generatedProfile ? (
                      <div className="space-y-2">
                        <p className="text-white/60 text-xs">You&apos;ll set colours, tone and name in the next screen.</p>
                        <button
                          onClick={() => {
                            router.push(`/onboarding?aiId=${generatedProfile.id}`);
                          }}
                          className="w-full px-6 py-3.5 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-600 text-white font-semibold hover:shadow-lg hover:shadow-indigo-500/30 transition-all duration-200"
                        >
                          Customise & deploy
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={generateProfile}
                          className="w-full px-6 py-3.5 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-600 text-white font-semibold hover:shadow-lg hover:shadow-indigo-500/30 transition-all duration-200"
                        >
                          {generateError ? "Try again" : "Build Your AI"}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
